import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { usePushNotifications } from '../hooks/usePushNotifications';

export const PushNotificationPrompt: React.FC = () => {
    const { t } = useTranslation();
    const { isStandalone } = usePWAInstall();
    const { isSupported, isSubscribed, subscribe, permission } = usePushNotifications();
    const [dismissed, setDismissed] = useState(() => localStorage.getItem('aura_push_dismissed') === 'true');

    // Only show if PWA (isStandalone), Push Supported, Not Subscribed, Not Dismissed, and Permission isn't already denied permanently
    if (!isStandalone || !isSupported || isSubscribed || dismissed || permission === 'denied') {
        return null;
    }

    const handleDismiss = () => {
        setDismissed(true);
        localStorage.setItem('aura_push_dismissed', 'true');
    };

    const handleSubscribe = async () => {
        const success = await subscribe();
        if (success) {
            setDismissed(true); // Don't show again once subscribed
        }
    };

    return (
        <div className="fixed inset-0 z-[100] p-4 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-[#121212] border border-primary/30 rounded-[2rem] p-6 shadow-[0_15px_50px_rgba(0,0,0,0.9)] w-full max-w-sm relative pointer-events-auto animate-in zoom-in-95 duration-300">
                <button 
                    onClick={handleDismiss} 
                    className="absolute top-4 right-4 text-white/40 hover:text-white bg-black/50 rounded-full h-7 w-7 flex items-center justify-center transition-colors hover:scale-110 active:scale-95 z-10"
                >
                    <span className="material-symbols-outlined text-sm font-bold">close</span>
                </button>
                <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-primary/20 to-black border border-primary/50 rounded-[18px] w-14 h-14 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(242,127,13,0.15)] flex-col">
                        <span className="material-symbols-outlined text-primary shadow-xl drop-shadow-[0_0_8px_rgba(242,127,13,1)]">notifications_active</span>
                    </div>
                    <div className="pt-1 pr-4">
                        <h4 className="text-white font-black text-sm uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                            {t('push.title')}
                        </h4>
                        <p className="text-white/60 text-[11px] leading-relaxed mb-5 font-medium" dangerouslySetInnerHTML={{ __html: t('push.desc') }} />
                        
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={handleSubscribe}
                                className="flex-1 bg-gradient-to-r from-primary to-primary/80 text-black font-black uppercase tracking-widest text-[10px] py-3.5 rounded-xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_5px_15px_rgba(242,127,13,0.3)]"
                            >
                                {t('push.accept')}
                            </button>
                            <button 
                                onClick={handleDismiss}
                                className="flex-1 bg-white/5 border border-white/10 text-white/60 font-black uppercase tracking-widest text-[10px] py-3.5 rounded-xl hover:bg-white/10 hover:text-white transition-all active:scale-95"
                            >
                                {t('push.decline')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
